import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import Exception from "core/exceptions/Exception";
import DefaultExceptionTypes from "core/exceptions/DefaultExceptionTypes";
import { ApiResponse } from "../../../api/types/ApiResponse";
import { ApiResponseHandler } from "../handleApiResponse/handleApiResponse";
import handleRequestError from "../handleRequestError/handleRequestError";

/** Message métier renvoyé par le back, quelle que soit la forme du rejet (400, 403, 409...). */
export function backendMessage(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }
  const { message, body } = error as {
    message?: unknown;
    body?: { message?: unknown } | null;
  };
  const candidate = typeof message === "string" ? message : body?.message;
  return typeof candidate === "string" && candidate.trim().length > 0
    ? candidate
    : undefined;
}

/**
 * Les refus du circuit KYB (séparation des tâches, rôle inadapté, dossier figé, pièces manquantes)
 * ne se comprennent que par leur message : `handleRequestError` le perd sur un 400, un 403 ou un 409
 * (il ne garde que le corps), on le recopie donc dans `message` pour que l'écran l'affiche.
 */
export function toKybException(
  error: unknown,
): Exception<unknown, DefaultExceptionTypes> {
  const exception = handleRequestError(error as ApiResponse<unknown>);
  // un service injoignable n'a pas de message à recopier : « Failed to fetch » n'apprend rien à l'agent
  if (exception.type === DefaultExceptionTypes.NETWORK_ERROR) {
    return exception;
  }
  const message = exception.message ?? backendMessage(error);
  return message ? { ...exception, message } : exception;
}

/** Variante de `executeRequest` qui garde le message du back dans l'exception (domaine KYB). */
export async function executeKybRequest<Dto, T>(
  call: () => Promise<ApiResponse<Dto>>,
  map: (data: Dto) => T,
): Promise<RepositoryResult<T>> {
  try {
    const response = await call();
    const result = ApiResponseHandler.handleResponse<Dto>(response);
    if (result.success && result.data !== undefined) {
      return RepositoryResultFactory.success(map(result.data));
    }
    return RepositoryResultFactory.error(
      ApiResponseHandler.createExceptionFromResponse(response),
    );
  } catch (error) {
    return RepositoryResultFactory.error(toKybException(error));
  }
}

/** Variante sans charge utile de `executeKybRequest`. */
export async function executeKybVoidRequest<Dto>(
  call: () => Promise<ApiResponse<Dto>>,
): Promise<RepositoryResult<void>> {
  try {
    const response = await call();
    const result = ApiResponseHandler.handleResponse<Dto>(response);
    if (result.success) {
      return RepositoryResultFactory.success(undefined);
    }
    return RepositoryResultFactory.error(
      ApiResponseHandler.createExceptionFromResponse(response),
    );
  } catch (error) {
    return RepositoryResultFactory.error(toKybException(error));
  }
}
